import type { ColorSystemOptions } from "@mui/material/styles";

import { createColorWithAlphaChannel } from "../helpers/createColorWithAlphaChannel";
import { createPaletteChannel } from "../helpers/createPaletteChannel";
import COLORS from "./colors.json";

// ----------------------------------------------------------------------

declare module "@mui/material/styles" {
  // eslint-disable-next-line @typescript-eslint/consistent-type-definitions
  interface CommonColors {
    blackChannel: string;
    whiteChannel: string;
  }
  // eslint-disable-next-line @typescript-eslint/consistent-type-definitions
  interface PaletteColor {
    darker: string;
    darkerChannel: string;
    lighter: string;
    lighterChannel: string;
  }
  // eslint-disable-next-line @typescript-eslint/consistent-type-definitions
  interface SimplePaletteColorOptions {
    darker: string;
    darkerChannel: string;
    lighter: string;
    lighterChannel: string;
  }
  // eslint-disable-next-line @typescript-eslint/consistent-type-definitions
  interface TypeBackground {
    neutral: string;
    neutralChannel: string;
  }
  // eslint-disable-next-line @typescript-eslint/consistent-type-definitions
  interface TypeText {
    disabledChannel: string;
  }
}

declare module "@mui/material/styles" {
  // eslint-disable-next-line @typescript-eslint/consistent-type-definitions
  interface ThemeVars {
    transitions: Theme["transitions"];
  }
}

declare module "@mui/material" {
  // eslint-disable-next-line @typescript-eslint/consistent-type-definitions
  interface Color {
    ["50Channel"]: string;
    ["100Channel"]: string;
    ["200Channel"]: string;
    ["300Channel"]: string;
    ["400Channel"]: string;
    ["500Channel"]: string;
    ["600Channel"]: string;
    ["700Channel"]: string;
    ["800Channel"]: string;
    ["900Channel"]: string;
  }
}

export type ColorType =
  | "error"
  | "info"
  | "primary"
  | "secondary"
  | "success"
  | "warning";

export const grey = createPaletteChannel(COLORS.grey);

export const primary = createPaletteChannel(COLORS.primary);

export const secondary = createPaletteChannel(COLORS.secondary);

export const info = createPaletteChannel(COLORS.info);

export const success = createPaletteChannel(COLORS.success);

export const warning = createPaletteChannel(COLORS.warning);

export const error = createPaletteChannel(COLORS.error);

export const common = createPaletteChannel(COLORS.common);

export const text = {
  light: createPaletteChannel({
    disabled: grey[500],
    primary: grey[800],
    secondary: grey[600],
  }),
};

export const background = {
  light: createPaletteChannel({
    default: grey[100],
    neutral: grey[200],
    paper: "#FFFFFF",
  }),
};

export const baseAction = {
  disabled: createColorWithAlphaChannel(grey["500Channel"], 0.8),
  disabledBackground: createColorWithAlphaChannel(grey["500Channel"], 0.24),
  disabledOpacity: 0.48,
  focus: createColorWithAlphaChannel(grey["500Channel"], 0.24),
  hover: createColorWithAlphaChannel(grey["500Channel"], 0.08),
  hoverOpacity: 0.08,
  selected: createColorWithAlphaChannel(grey["500Channel"], 0.16),
};

export const action = {
  light: { ...baseAction, active: grey[600] },
};

export const basePalette = {
  action,
  common,
  divider: createColorWithAlphaChannel(grey["500Channel"], 0.2),
  error,
  grey,
  info,
  primary,
  secondary,
  success,
  warning,
};

export const lightPalette = {
  ...basePalette,
  action: action.light,
  background: background.light,
  text: text.light,
};

export const colorSchemes: Partial<Record<"light", ColorSystemOptions>> = {
  light: { palette: lightPalette },
};
